// App core shared by every page module: API client, dialogs, toasts, formatting, catalogs, hash router,
// realtime socket. Pages register with App.page(name, def) and render into the shell's main area.
'use strict';
(function () {
  const App = window.App = window.App || {};
  const t = App.t;

  // ── basics ──
  App.esc = (value) => String(value == null ? '' : value).replace(/[&<>"']/g,
    (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
  App.$ = (selector, root) => (root || document).querySelector(selector);
  App.$$ = (selector, root) => [...(root || document).querySelectorAll(selector)];
  App.me = null;
  App.viewAs = null;               // admin impersonation: { id, username } or null

  // ── API ──
  // Admin "view as" applies to the per-user list endpoints only; mutations address resources by id.
  const VIEW_AS_PATHS = ['/api/characters', '/api/machines', '/api/licenses', '/api/templates', '/api/logs'];
  App.api = async function (path, options) {
    const opts = options || {};
    const method = (opts.method || 'GET').toUpperCase();
    const query = Object.assign({}, opts.query || {});
    if (App.viewAs && method === 'GET' && VIEW_AS_PATHS.includes(path)) query.user_id = App.viewAs.id;
    const qs = Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => encodeURIComponent(k) + '=' + encodeURIComponent(v)).join('&');
    let response;
    try {
      response = await fetch(path + (qs ? (path.includes('?') ? '&' : '?') + qs : ''), {
        method, credentials: 'same-origin',
        headers: opts.body !== undefined ? { 'Content-Type': 'application/json' } : {},
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined
      });
    } catch (_) {
      return { ok: false, status: 0, data: { err: 'network' } };
    }
    let data = null;
    try { data = await response.json(); } catch (_) { data = null; }
    if (response.status === 401 && path !== '/api/login' && path !== '/api/me' && App.onUnauthorized) App.onUnauthorized();
    return { ok: response.ok, status: response.status, data: data || {} };
  };
  // Localized text for an API error body ({err, msg}) or a code string.
  App.errorText = function (error) {
    const code = typeof error === 'string' ? error : (error && error.err) || '';
    const key = 'e_' + code;
    if (code && App.i18n.has(key)) return t(key);
    if (error && typeof error === 'object' && error.msg) return String(error.msg);
    return t('e_fail');
  };

  // ── toasts ──
  App.toast = function (message, kind) {
    let stack = document.getElementById('toasts');
    if (!stack) { stack = document.createElement('div'); stack.id = 'toasts'; stack.className = 'toasts'; document.body.appendChild(stack); }
    const el = document.createElement('div');
    el.className = 'toast ' + (kind || 'ok');
    el.setAttribute('role', kind === 'error' ? 'alert' : 'status');
    el.textContent = message;
    stack.appendChild(el);
    setTimeout(() => { el.classList.add('out'); setTimeout(() => el.remove(), 250); }, kind === 'error' ? 5000 : 2800);
  };

  // ── dialogs ──
  // body: HTML string or Node. actions: [{label, kind:'primary'|'secondary'|'danger'|'ghost', onClick(api) -> false keeps open}].
  // canClose(): return false to veto closing by Esc / backdrop / close button (e.g. unsaved changes).
  App.dialog = function (opts) {
    const back = document.createElement('div');
    back.className = 'dialog-back';
    const box = document.createElement('div');
    box.className = 'dialog' + (opts.wide ? ' wide' : '') + (opts.className ? ' ' + opts.className : '');
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.innerHTML = `<div class="dialog-head"><h2>${App.esc(opts.title || '')}</h2>
      <button type="button" class="btn ghost icon" data-close aria-label="${App.esc(t('close'))}">✕</button></div>
      <div class="dialog-body"></div>${opts.actions && opts.actions.length ? '<div class="dialog-foot"></div>' : ''}`;
    const body = box.querySelector('.dialog-body');
    if (typeof opts.body === 'string') body.innerHTML = opts.body; else if (opts.body) body.appendChild(opts.body);
    back.appendChild(box);
    document.body.appendChild(back);
    const previous = document.activeElement;
    let closed = false;
    const api = {
      el: box, body,
      close(force) {
        if (closed) return;
        if (!force && opts.canClose && opts.canClose() === false) return;
        closed = true;
        document.removeEventListener('keydown', onKey, true);
        back.remove();
        if (previous && previous.focus) previous.focus();
        if (opts.onClose) opts.onClose();
      },
      busy(on) { App.$$('.dialog-foot button', box).forEach(b => { b.disabled = !!on; }); }
    };
    const onKey = (event) => {
      if (event.key === 'Escape' && document.body.lastElementChild === back) { event.stopPropagation(); api.close(); }
    };
    document.addEventListener('keydown', onKey, true);
    back.addEventListener('mousedown', (event) => { if (event.target === back) api.close(); });
    box.querySelector('[data-close]').onclick = () => api.close();
    if (opts.actions) {
      const foot = box.querySelector('.dialog-foot');
      for (const action of opts.actions) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn ' + (action.kind || 'secondary');
        button.textContent = action.label;
        button.onclick = async () => {
          if (!action.onClick) return api.close(true);
          api.busy(true);
          let keep;
          try { keep = await action.onClick(api); } finally { if (!closed) api.busy(false); }
          if (keep !== false) api.close(true);
        };
        foot.appendChild(button);
      }
    }
    const focusTarget = box.querySelector('[autofocus], .dialog-body input:not([type=hidden]), .dialog-body select, .dialog-body textarea, .dialog-foot .btn.primary, .dialog-foot .btn.danger');
    if (focusTarget) setTimeout(() => focusTarget.focus(), 0);
    return api;
  };
  App.confirm = function (opts) {
    return new Promise((resolve) => {
      let answered = false;
      App.dialog({
        title: opts.title || t('confirm'),
        body: `<p class="dialog-text">${App.esc(opts.message || '')}</p>${opts.detail ? `<p class="muted small">${App.esc(opts.detail)}</p>` : ''}`,
        className: 'narrow',
        actions: [
          { label: opts.cancel || t('cancel'), kind: 'secondary' },
          { label: opts.confirm || t('confirm'), kind: opts.danger ? 'danger' : 'primary', onClick: () => { answered = true; resolve(true); } }
        ],
        onClose: () => { if (!answered) resolve(false); }
      });
    });
  };
  App.copy = async function (text) {
    try { await navigator.clipboard.writeText(String(text)); App.toast(t('copied')); }
    catch (_) { App.toast(t('e_fail'), 'error'); }
  };

  // ── formatting ──
  const pad = (n) => String(n).padStart(2, '0');
  App.fmt = {
    date(ts) { if (!ts) return '—'; const d = new Date(Number(ts)); return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); },
    dateTime(ts) { if (!ts) return '—'; const d = new Date(Number(ts)); return App.fmt.date(ts) + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes()); },
    rel(ts) {
      if (!ts) return '—';
      const s = Math.max(0, (Date.now() - Number(ts)) / 1000);
      if (s < 5) return t('just_now');
      if (s < 60) return t('secs_ago', Math.floor(s));
      if (s < 3600) return t('mins_ago', Math.floor(s / 60));
      if (s < 86400) return t('hours_ago', Math.floor(s / 3600));
      return t('days_ago', Math.floor(s / 86400));
    },
    num(n) { return n == null || n === '' || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString(); },
    remain(sec) {
      const v = Math.max(0, Math.round(Number(sec) || 0));
      const h = Math.floor(v / 3600), m = Math.floor(v % 3600 / 60), s = v % 60;
      return h ? h + ':' + pad(m) + ':' + pad(s) : m + ':' + pad(s);
    },
    pct(a, b) { a = +a || 0; b = +b || 0; return b > 0 ? Math.max(0, Math.min(100, a / b * 100)) : 0; }
  };

  // License status chip shared by dashboard, machines, licenses and admin pages.
  const LICENSE_TONE = { unregistered: 'neutral', unused: 'info', waiting: 'warn', active: 'ok', expired: 'bad', revoked: 'bad' };
  App.licenseBadge = function (license) {
    if (!license) return `<span class="badge neutral">${App.esc(t('lic_none'))}</span>`;
    const tone = LICENSE_TONE[license.status] || 'neutral';
    let text = t('lic_' + license.status);
    if (license.status === 'active' && license.remaining_days != null) text = t('lic_days_left', license.remaining_days);
    return `<span class="badge ${tone}">${App.esc(text)}</span>`;
  };

  // Supply shortage alarm from the play telemetry (`supply_short`: {adena, need, items:[{code, have, min, depart,
  // price, need, reason: 'adena'|'purchase', blocking}]}). Blocking rows hold the departure (the character waits in
  // town); the others were bought as far as the wallet allowed and the hunt goes on.
  App.supplyShortBanner = function (short) {
    if (!short || !Array.isArray(short.items) || !short.items.length) return '';
    const blocking = short.items.some(i => i.blocking);
    const rows = short.items.map(i => {
      const name = App.cat && App.cat.item ? App.cat.item(i.code) : String(i.code);
      const min = i.blocking && i.depart != null ? i.depart : i.min;
      return App.esc(name) + ' ' + App.esc(App.fmt.num(i.have)) + '/' + App.esc(App.fmt.num(min)) +
        (i.reason === 'purchase' ? ' (' + App.esc(t('short_purchase_failed')) + ')' : '');
    }).join(', ');
    const money = short.need > 0 ? ' · ' + App.esc(t('short_adena', App.fmt.num(short.need), App.fmt.num(short.adena))) : '';
    return `<div class="ch-cta banner ${blocking ? 'error' : 'warn'}" role="status">⚠ <span>${App.esc(t(blocking ? 'short_blocking' : 'short_partial'))}: ${rows}${money}</span></div>`;
  };

  // ── catalogs (items, maps, monsters, skills, sprites, regions, grids) ──
  const CATALOGS = ['items', 'maps', 'monsters', 'skills', 'item_icons', 'skill_icons', 'mob_sprites', 'regions',
    'gridmap', 'isomap', 'npc_roles', 'ref_buffs'];
  App.catalog = {};
  const catVersion = (name) => (window.__CATV && window.__CATV[name]) || '';
  const zh = () => App.lang() === 'zh-CN';
  const localName = (entry, fallback) => entry ? ((zh() && entry.name_tw) ? entry.name_tw : (entry.name || fallback)) : fallback;
  function atlasStyle(sheet, ic, cat, width, height) {
    const atlas = App.cat.asset(sheet.atlas[ic.a], cat);
    const scale = width / (ic.w || 64), big = (sheet.size || 2048) * scale;
    return `background-image:url(${atlas});background-repeat:no-repeat;background-position:-${Math.round(ic.x * scale)}px -${Math.round(ic.y * scale)}px;` +
      `background-size:${Math.round(big)}px ${Math.round(big)}px;width:${Math.round(width)}px;height:${Math.round(height)}px`;
  }
  let thumbSet = null, buffIndex = null;
  App.cat = {
    async load() {
      await Promise.all(CATALOGS.map(async (name) => {
        try {
          const v = catVersion(name);
          App.catalog[name] = await (await fetch('/catalog/' + name + '.json' + (v ? '?v=' + v : ''))).json();
        } catch (_) { App.catalog[name] = {}; }
      }));
    },
    asset(rel, cat) { const v = catVersion(cat); return '/assets/' + rel + (v ? '?v=' + v : ''); },
    item(code) { const all = (App.catalog.items && App.catalog.items.items) || {}; return localName(all[code], String(code)); },
    // Agent inventory entry -> display name: catalog first, then the agent's name unless it is a localization key.
    itemDisplay(it) {
      const all = (App.catalog.items && App.catalog.items.items) || {};
      if (all[it.code] && all[it.code].name) return localName(all[it.code], String(it.code));
      const name = String(it.name == null ? '' : it.name).trim();
      return !name || /^\$?\d+$/.test(name) ? '#' + it.code : name;
    },
    map(id) { const all = (App.catalog.maps && App.catalog.maps.maps) || {}; return localName(all[id], String(id)); },
    region(mapId, x, y) {
      const regions = (App.catalog.regions && App.catalog.regions.regions) || [];
      const mid = Number(mapId); let fallback = null;
      for (const r of regions) {
        if (r.map !== mid) continue;
        const name = localName(r, String(r.id));
        if (r.l != null && x >= r.l && x <= r.r && y >= r.t && y <= r.b) return name;
        if (r.l == null && !fallback) fallback = name;
      }
      return fallback;
    },
    regionById(id) { return ((App.catalog.regions && App.catalog.regions.regions) || []).find(r => String(r.id) === String(id)) || null; },
    mapLabel(pos) {
      if (!pos || pos.map_id == null) return '—';
      const name = App.cat.map(pos.map_id);
      const region = App.cat.region(pos.map_id, pos.x || 0, pos.y || 0);
      return region ? name + ' · ' + region : name;
    },
    monster(code) { const all = (App.catalog.monsters && App.catalog.monsters.monsters) || {}; return localName(all[code], String(code)); },
    skill(id) { const sk = App.catalog.skills && App.catalog.skills.skills && App.catalog.skills.skills[id]; return sk ? localName(sk, '#' + id) : '#' + id; },
    spellDesc(id) {
      const sk = App.catalog.skills && App.catalog.skills.skills && App.catalog.skills.skills[id];
      if (!sk) return '';
      const d = (zh() && sk.desc_tw) ? sk.desc_tw : sk.desc;
      return d ? String(d).replace(/\\f./g, '').replace(/\\n/g, '\n').trim() : '';
    },
    buff(id) {
      if (buffIndex === null) {
        buffIndex = {};
        for (const b of ((App.catalog.ref_buffs && App.catalog.ref_buffs.buffs) || [])) if (b && b.buffid != null) buffIndex[b.buffid] = localName(b, b.name_tw);
      }
      return buffIndex[id] || ('#' + id);
    },
    itemIcon(code, size) {
      const C = App.catalog;
      const iconId = ((C.items && C.items.items && C.items.items[code]) || {}).icon;
      const ic = iconId != null && C.item_icons && C.item_icons.icons && C.item_icons.icons[iconId];
      const s = size || 42;
      return ic ? atlasStyle(C.item_icons, ic, 'item_icons', s, s) : null;
    },
    spellIconFrame(frame, size) {
      const C = App.catalog;
      const ic = frame != null && C.skill_icons && C.skill_icons.icons && C.skill_icons.icons[frame];
      const s = size || 42;
      return ic ? atlasStyle(C.skill_icons, ic, 'skill_icons', s, s) : null;
    },
    skillIcon(id, size) {
      const sk = (App.catalog.skills && App.catalog.skills.skills && App.catalog.skills.skills[id]) || {};
      return sk.icon != null ? App.cat.spellIconFrame(sk.icon, size) : null;
    },
    mobSprite(spriteId, size) {
      const C = App.catalog;
      const ic = spriteId != null && C.mob_sprites && C.mob_sprites.icons && C.mob_sprites.icons[spriteId];
      if (!ic) return null;
      const s = (size || 42) / Math.max(ic.w || 1, ic.h || 1);
      const atlas = App.cat.asset(C.mob_sprites.atlas[ic.a], 'mob_sprites'), big = (C.mob_sprites.size || 2048) * s;
      return `background-image:url(${atlas});background-repeat:no-repeat;background-position:-${Math.round(ic.x * s)}px -${Math.round(ic.y * s)}px;` +
        `background-size:${Math.round(big)}px ${Math.round(big)}px;width:${Math.round(ic.w * s)}px;height:${Math.round(ic.h * s)}px`;
    },
    spriteThumb(id, height) {
      if (thumbSet === null) thumbSet = new Set(((App.catalog.mob_sprites && App.catalog.mob_sprites.thumbs) || []).map(Number));
      if (id == null || !thumbSet.has(Number(id))) return '';
      return `<img class="sprthumb" loading="lazy" alt="" src="${App.cat.asset('sprite/thumb/' + Number(id) + '.png', 'mob_sprites')}" style="max-height:${height || 48}px" onerror="this.remove()">`;
    },
    gridInfo(mapId) { const g = App.catalog.gridmap && App.catalog.gridmap.maps; return (g && g[mapId]) || null; },
    isoInfo(mapId) { const g = App.catalog.isomap && App.catalog.isomap.maps; return (g && g[mapId]) || null; }
  };

  // ── catalog search picker (single value or chip adder) ──
  // Kinds store either a catalog code (item, map, region, skill, buff: numbers) or a game name string
  // (monster, itemName, npc, regionName: the Korean name the client reports). Name kinds also accept a typed
  // name that is not in the catalog. Region kinds list only regions of the map picked in the same container.
  const NAME_KINDS = new Set(['monster', 'itemName', 'npc', 'regionName']);
  const MAP_SCOPE = '.hm-row, .hf-row, .hf-travel-body, .hf-sub, .hf-fields, .form-grid, form';
  let pickIndex = null;
  function pickEntries(kind) {
    if (!pickIndex || pickIndex._lang !== App.lang()) pickIndex = { _lang: App.lang() };
    if (pickIndex[kind]) return pickIndex[kind];
    const C = App.catalog;
    const list = [];
    const seen = new Set();
    const add = (entry) => { const key = String(entry.code) + '\u0000' + (entry.map == null ? '' : entry.map); if (!seen.has(key)) { seen.add(key); list.push(entry); } };
    const items = (C.items && C.items.items) || {};
    const regions = (C.regions && C.regions.regions) || [];
    if (kind === 'item') for (const code in items) add({ code: Number(code), name: items[code].name || code, tw: items[code].name_tw || '', icon: code });
    if (kind === 'itemName') for (const code in items) if (items[code].name) add({ code: items[code].name, name: items[code].name, tw: items[code].name_tw || '', icon: code });
    if (kind === 'map') { const maps = (C.maps && C.maps.maps) || {}; for (const id in maps) add({ code: Number(id), name: maps[id].name || id, tw: maps[id].name_tw || '' }); }
    if (kind === 'monster') { const mons = (C.monsters && C.monsters.monsters) || {}; for (const code in mons) if (mons[code].name) add({ code: mons[code].name, name: mons[code].name, tw: mons[code].name_tw || '', spr: mons[code].sprite }); }
    if (kind === 'region') for (const r of regions) add({ code: r.id, name: r.name || String(r.id), tw: r.name_tw || '', map: r.map });
    if (kind === 'regionName') for (const r of regions) if (r.name) add({ code: r.name, name: r.name, tw: r.name_tw || '', map: r.map });
    if (kind === 'skill') { const sk = (C.skills && C.skills.skills) || {}; for (const id in sk) if (sk[id].name || sk[id].name_tw) add({ code: Number(id), name: sk[id].name || ('#' + id), tw: sk[id].name_tw || '' }); }
    if (kind === 'buff') for (const b of ((C.ref_buffs && C.ref_buffs.buffs) || [])) if (b && b.buffid != null) add({ code: Number(b.buffid), name: b.name || b.name_tw || ('#' + b.buffid), tw: b.name_tw || '' });
    if (kind === 'npc') for (const n of ((C.npc_roles && C.npc_roles.roles) || [])) if (n.name) add({ code: n.name, name: n.name, tw: n.name_tw || '', role: n.role_label || n.role || '', map: n.map });
    pickIndex[kind] = list;
    return list;
  }
  const pickLocal = (entry) => (zh() && entry.tw) ? entry.tw : entry.name;
  function pickFind(kind, value) {
    if (value == null || value === '') return null;
    return pickEntries(kind).find(e => String(e.code) === String(value)) || null;
  }
  function pickDecor(kind, entry, size) {
    if (!entry) return '';
    if (kind === 'monster' && entry.spr != null) { const st = App.cat.mobSprite(entry.spr, size); return `<span class="pick-ico">${st ? `<span style="${st}"></span>` : ''}</span>`; }
    if ((kind === 'item' || kind === 'itemName') && entry.icon != null) { const st = App.cat.itemIcon(entry.icon, size); return st ? `<span class="pick-ico"><span style="${st}"></span></span>` : ''; }
    return '';
  }
  function pickSecondary(kind, entry) {
    if (kind === 'npc') return [entry.role, entry.map != null ? App.cat.map(entry.map) : ''].filter(Boolean).join(' · ');
    if (kind === 'regionName' || kind === 'region') return (kind === 'region' ? '#' + entry.code + ' · ' : '') + (entry.map != null ? App.cat.map(entry.map) : '');
    if (NAME_KINDS.has(kind)) return '';
    return '#' + entry.code;
  }
  function scopedMap(pick) {
    const scope = pick.closest(MAP_SCOPE);
    const mapPick = scope && [...scope.querySelectorAll('.pick[data-kind="map"]')].find(p => p !== pick);
    return mapPick ? App.picker.raw(mapPick) : '';
  }
  function pickMenu(pick) {
    const kind = pick.dataset.kind;
    const input = pick.querySelector('.pick-in');
    const menu = pick.querySelector('.pick-menu');
    const text = input.value.trim();
    const q = text.toLowerCase();
    let list = pickEntries(kind);
    if (kind === 'region' || kind === 'regionName') { const mid = scopedMap(pick); if (mid !== '') list = list.filter(e => String(e.map) === String(mid)); }
    const hits = (q ? list.filter(e => (e.name + ' ' + e.tw).toLowerCase().includes(q)) : list).slice(0, 60);
    const exact = hits.some(e => e.name === text || e.tw === text);
    let html = '';
    if (text && NAME_KINDS.has(kind) && !exact) {
      html += `<div class="pick-opt pick-free" data-code="${App.esc(text)}"><span class="pick-lab">${App.esc(t('pick_use_text', text))}</span></div>`;
    }
    html += hits.map(e => `<div class="pick-opt" data-code="${App.esc(e.code)}"><span class="pick-lab">${pickDecor(kind, e, 24)}<span class="truncate">${App.esc(pickLocal(e))}</span></span>` +
      `<small>${App.esc(pickSecondary(kind, e))}</small></div>`).join('');
    menu.innerHTML = html || `<div class="pick-opt pick-none">${App.esc(t('pick_no_match'))}</div>`;
    const first = menu.querySelector('.pick-opt[data-code]');
    if (first && q) first.classList.add('active');
    menu.hidden = false;
  }
  App.picker = {
    isName: (kind) => NAME_KINDS.has(kind),
    // chips: the input only adds values (onSelect), there is no stored single value.
    html(kind, value, opts) {
      const o = opts || {};
      const ph = o.placeholder || t(o.chips ? 'pick_add_' + kind : 'pick_ph_' + kind);
      const shown = o.chips ? '' : App.picker.label(kind, value);
      return `<span class="pick${o.chips ? ' pick-chipadd' : ''}" data-kind="${kind}"${o.id ? ` id="${App.esc(o.id)}"` : ''}>` +
        `<input class="pick-in" autocomplete="off" role="combobox" aria-autocomplete="list" placeholder="${App.esc(ph)}" value="${App.esc(shown)}"${o.disabled ? ' disabled' : ''}>` +
        (o.chips ? '' : `<input type="hidden" class="pick-val" value="${value == null ? '' : App.esc(value)}">`) +
        `<div class="pick-menu" role="listbox" hidden></div></span>`;
    },
    label(kind, value) {
      if (value == null || value === '') return '';
      const entry = pickFind(kind, value);
      return entry ? pickLocal(entry) : String(value);
    },
    decor(kind, value, size) { return pickDecor(kind, pickFind(kind, value), size || 22); },
    // Raw string value: the chosen code, or for name kinds the typed text.
    raw(pick) {
      if (!pick) return '';
      const hidden = pick.querySelector('.pick-val');
      const value = hidden ? hidden.value.trim() : '';
      if (value !== '') return value;
      return NAME_KINDS.has(pick.dataset.kind) ? pick.querySelector('.pick-in').value.trim() : '';
    },
    // Typed value for settings: numbers for code kinds, strings for name kinds, undefined when empty.
    read(pick) { const raw = App.picker.raw(pick); return App.picker.cast(pick && pick.dataset.kind, raw); },
    cast(kind, raw) {
      if (raw === '' || raw == null) return undefined;
      if (NAME_KINDS.has(kind)) return String(raw);
      const n = Number(raw); return Number.isNaN(n) ? raw : n;
    },
    set(pick, value) {
      const hidden = pick.querySelector('.pick-val');
      if (hidden) hidden.value = value == null ? '' : String(value);
      pick.querySelector('.pick-in').value = App.picker.label(pick.dataset.kind, value);
    },
    // Event delegation for every .pick inside root. onSelect(pick, value) runs after a choice (chip adders are
    // cleared afterwards); onType(pick) runs while typing into a single picker.
    bind(root, opts) {
      const o = opts || {};
      const choose = (pick, opt) => {
        if (!opt || opt.dataset.code == null || o.readOnly) return;
        const kind = pick.dataset.kind;
        const value = App.picker.cast(kind, opt.dataset.code);
        pick.querySelector('.pick-menu').hidden = true;
        if (pick.classList.contains('pick-chipadd')) pick.querySelector('.pick-in').value = '';
        else App.picker.set(pick, value);
        if (o.onSelect) o.onSelect(pick, value);
      };
      const onFocus = (e) => { if (e.target.classList && e.target.classList.contains('pick-in') && !o.readOnly) pickMenu(e.target.closest('.pick')); };
      const onBlur = (e) => {
        if (!(e.target.classList && e.target.classList.contains('pick-in'))) return;
        const pick = e.target.closest('.pick');
        setTimeout(() => { const menu = pick.querySelector('.pick-menu'); if (menu) menu.hidden = true; }, 150);
      };
      const onInput = (e) => {
        if (!(e.target.classList && e.target.classList.contains('pick-in')) || o.readOnly) return;
        const pick = e.target.closest('.pick');
        const hidden = pick.querySelector('.pick-val');
        if (hidden) hidden.value = '';
        pickMenu(pick);
        if (!pick.classList.contains('pick-chipadd') && o.onType) o.onType(pick);
      };
      const onKey = (e) => {
        if (!(e.target.classList && e.target.classList.contains('pick-in')) || o.readOnly) return;
        const pick = e.target.closest('.pick');
        const menu = pick.querySelector('.pick-menu');
        const opts2 = [...menu.querySelectorAll('.pick-opt[data-code]')];
        const active = menu.querySelector('.pick-opt.active');
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          if (menu.hidden) { pickMenu(pick); return; }
          const i = opts2.indexOf(active);
          const next = opts2[e.key === 'ArrowDown' ? Math.min(opts2.length - 1, i + 1) : Math.max(0, i - 1)];
          if (active) active.classList.remove('active');
          if (next) { next.classList.add('active'); next.scrollIntoView({ block: 'nearest' }); }
        } else if (e.key === 'Enter') {
          if (menu.hidden && !pick.classList.contains('pick-chipadd')) return;
          e.preventDefault();
          choose(pick, active || (opts2.length === 1 ? opts2[0] : null));
        } else if (e.key === 'Escape' && !menu.hidden) {
          e.stopPropagation();
          menu.hidden = true;
        }
      };
      const onDown = (e) => {
        const opt = e.target.closest && e.target.closest('.pick-opt');
        if (!opt || !root.contains(opt)) return;
        e.preventDefault();
        choose(opt.closest('.pick'), opt);
      };
      root.addEventListener('focusin', onFocus);
      root.addEventListener('focusout', onBlur);
      root.addEventListener('input', onInput);
      root.addEventListener('keydown', onKey);
      root.addEventListener('mousedown', onDown);
      return () => {
        root.removeEventListener('focusin', onFocus); root.removeEventListener('focusout', onBlur);
        root.removeEventListener('input', onInput); root.removeEventListener('keydown', onKey);
        root.removeEventListener('mousedown', onDown);
      };
    }
  };

  // ── multi-select rows (checkbox column, Shift range, Ctrl/Cmd toggle, select-all header) ──
  // Markup: rows carry data-sel="<id>" and contain input.sel-row; the header has input.sel-all. Clicking a row's
  // background selects only that row, Ctrl/Cmd+click toggles it, Shift+click selects the range from the last
  // anchor; buttons, links and form controls inside a row keep their own behaviour. Esc clears the selection.
  App.selectable = function (container, opts) {
    const o = opts || {};
    const selected = new Set();
    let anchor = null;
    const rows = () => App.$$('[data-sel]', container).filter(r => !r.closest('.hidden') && r.offsetParent !== null);
    const paint = () => {
      const all = rows();
      for (const row of App.$$('[data-sel]', container)) {
        const on = selected.has(row.dataset.sel);
        row.classList.toggle('selected', on);
        const box = row.querySelector('input.sel-row'); if (box) box.checked = on;
      }
      const head = container.querySelector('input.sel-all');
      if (head) {
        const count = all.filter(r => selected.has(r.dataset.sel)).length;
        head.checked = all.length > 0 && count === all.length;
        head.indeterminate = count > 0 && count < all.length;
      }
      if (o.onChange) o.onChange([...selected]);
    };
    const range = (to) => {
      const all = rows().map(r => r.dataset.sel);
      const a = all.indexOf(anchor), b = all.indexOf(to);
      if (a < 0 || b < 0) { selected.add(to); return; }
      for (const id of all.slice(Math.min(a, b), Math.max(a, b) + 1)) selected.add(id);
    };
    const onClick = (e) => {
      const head = e.target.closest('input.sel-all');
      if (head) { if (head.checked) rows().forEach(r => selected.add(r.dataset.sel)); else selected.clear(); anchor = null; paint(); return; }
      const row = e.target.closest('[data-sel]');
      if (!row || !container.contains(row)) return;
      const id = row.dataset.sel;
      const onBox = !!e.target.closest('input.sel-row');
      if (!onBox && e.target.closest('button, a, input, select, textarea, label, .pick')) return;
      if (e.shiftKey && anchor) { if (!(e.ctrlKey || e.metaKey)) selected.clear(); range(id); }
      else if (onBox || e.ctrlKey || e.metaKey) { if (selected.has(id)) selected.delete(id); else selected.add(id); anchor = id; }
      else { selected.clear(); selected.add(id); anchor = id; }
      paint();
    };
    const onKey = (e) => { if (e.key === 'Escape' && selected.size && !document.querySelector('.dialog-back')) { selected.clear(); paint(); } };
    container.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return {
      ids: () => [...selected],
      clear() { selected.clear(); anchor = null; paint(); },
      // Call after re-rendering rows: drops ids that are no longer listed.
      refresh() { const present = new Set(App.$$('[data-sel]', container).map(r => r.dataset.sel)); for (const id of [...selected]) if (!present.has(id)) selected.delete(id); paint(); },
      destroy() { container.removeEventListener('click', onClick); document.removeEventListener('keydown', onKey); }
    };
  };

  // ── pages and hash router (#/name/param/...) ──
  const pages = new Map();
  let current = null;               // { name, cleanup }
  let leaveGuard = null;            // () => true when the current page has unsaved changes
  App.pages = pages;
  App.page = function (name, def) { pages.set(name, Object.assign({ name }, def)); };
  App.setLeaveGuard = function (fn) { leaveGuard = fn || null; };
  App.navigate = function (hash) { if (location.hash === hash) App.route(); else location.hash = hash; };
  App.parseHash = function () {
    const parts = location.hash.replace(/^#\/?/, '').split('/').filter(Boolean).map(decodeURIComponent);
    return { name: parts[0] || 'dashboard', params: parts.slice(1) };
  };
  let lastHash = location.hash;
  let guarding = false;
  App.route = async function () {
    if (guarding) return;
    const { name, params } = App.parseHash();
    const sameRoute = current && current.name === name && current.key === location.hash;
    if (leaveGuard && !sameRoute && leaveGuard()) {
      guarding = true;
      const target = location.hash;
      history.replaceState(null, '', lastHash || '#/');
      const leave = await App.confirm({ title: t('unsaved_title'), message: t('unsaved_message'), confirm: t('discard'), cancel: t('keep_editing'), danger: true });
      guarding = false;
      if (!leave) return;
      leaveGuard = null;
      history.replaceState(null, '', target);
    }
    lastHash = location.hash;
    const page = pages.get(name);
    const root = document.getElementById('page');
    if (!root) return;
    if (current && current.cleanup) { try { current.cleanup(); } catch (error) { console.error(error); } }
    leaveGuard = null;
    current = { name, key: location.hash, cleanup: null };
    document.querySelectorAll('[data-nav]').forEach(a => a.classList.toggle('active', a.dataset.nav === name || (page && page.navAs === a.dataset.nav)));
    document.body.classList.remove('nav-open');
    if (!page || (page.admin && (!App.me || App.me.role !== 'admin'))) {
      root.innerHTML = `<div class="empty-state"><p>${App.esc(t('page_not_found'))}</p><a class="btn secondary" href="#/">${App.esc(t('nav_dashboard'))}</a></div>`;
      return;
    }
    document.title = (page.title ? page.title() + ' · ' : '') + t('brand');
    root.innerHTML = '';
    root.scrollTop = 0;
    window.scrollTo(0, 0);
    try {
      const cleanup = await page.render(root, params);
      if (current && current.key === location.hash) current.cleanup = typeof cleanup === 'function' ? cleanup : null;
    } catch (error) {
      console.error(error);
      root.innerHTML = `<div class="empty-state"><p>${App.esc(t('e_fail'))}</p></div>`;
    }
  };
  window.addEventListener('beforeunload', (event) => { if (leaveGuard && leaveGuard()) { event.preventDefault(); event.returnValue = ''; } });

  // ── realtime socket (browser channel) ──
  const handlers = new Map();
  const ws = { socket: null, backoff: 1000, ids: [], state: 'connecting', stopped: false };
  App.ws = {
    on(type, fn) { if (!handlers.has(type)) handlers.set(type, new Set()); handlers.get(type).add(fn); return () => handlers.get(type).delete(fn); },
    subscribe(ids) { ws.ids = [...new Set((ids || []).map(Number).filter(Number.isInteger))].slice(0, 200); send({ t: 'subscribe', ids: ws.ids }); },
    send(message) { send(message); },
    state() { return ws.state; },
    start() { ws.stopped = false; connect(); },
    stop() { ws.stopped = true; if (ws.socket) try { ws.socket.close(); } catch (_) {} }
  };
  function send(message) { if (ws.socket && ws.socket.readyState === 1) ws.socket.send(JSON.stringify(message)); }
  function setState(state) { ws.state = state; emit('status', { state }); }
  function emit(type, message) { const set = handlers.get(type); if (set) for (const fn of set) { try { fn(message); } catch (error) { console.error(error); } } }
  function connect() {
    if (ws.stopped) return;
    const socket = new WebSocket((location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host + '/ws');
    ws.socket = socket;
    socket.onopen = () => { ws.backoff = 1000; setState('live'); emit('open', {}); if (ws.ids.length) send({ t: 'subscribe', ids: ws.ids }); };
    socket.onmessage = (event) => { let m; try { m = JSON.parse(event.data); } catch (_) { return; } if (m && m.t) emit(m.t, m); };
    socket.onclose = async () => {
      if (ws.socket !== socket || ws.stopped) return;
      setState('reconnecting');
      // A dead session also fails the upgrade: confirm with /api/me so the user sees the login screen.
      const me = await App.api('/api/me');
      if (me.status === 401) { if (App.onUnauthorized) App.onUnauthorized(); return; }
      setTimeout(connect, ws.backoff);
      ws.backoff = Math.min(15000, ws.backoff * 2);
    };
  }
})();
